<template>
  <section class="scheduled-instances">
    <!-- header and stats -->
    <div class="group -flex -between">
      <h1 class="heading -secondary">Scheduled Instances</h1>
      <p class="plan-stats">
        <span>
          In Progress: <strong>{{ groupings.inProgress ? 'Yes' : 'No' }}</strong>
        </span>
        <span>
          Upcoming: <strong>{{ groupings.upcomingCount }}</strong>
        </span>
        <span>
          Past: <strong>{{ groupings.pastCount }}</strong>
        </span>
        <span class="completed">
          <span>Completed:</span>
          <strong
            ><i>{{ groupings.completedCount }}</i></strong
          >
        </span>
      </p>
    </div>

    <!-- user schedule groupings -->
    <!-- error -->
    <div v-if="error" class="purchased-status-row">
      <callout type="error">
        <div>{{ error }}</div>
      </callout>
    </div>

    <!-- no results, special "add" message -->
    <div v-if="resultsReady && groupings.total === 0" class="purchased-status-row">
      <p>Not scheduled yet. Add to calendar to get started!</p>
      <program-actions
        :entity-id="planId"
        :entity-category="entityCategory"
        :entity-title="planTitle"
        :button-reduce="false"
      />
    </div>

    <!-- has results, iterate ordered keys first, then instances -->
    <div v-if="resultsReady && groupings.total > 0">
      <template v-for="groupLabel in groupLabels" :key="groupLabel.key">
        <div
          v-for="instance in groupings.scheduledInstances[groupLabel.key]"
          :key="`${groupLabel.key}_instance${instance.id}`"
          class="purchased-status-row top-border centered-container-content"
        >
          <div class="status-row-date">
            <span class="time">
              <i class="material-symbols-outlined" aria-hidden="true">schedule</i>
              {{ groupLabel.name }}
            </span>
            <span class="schedule">
              <a
                v-if="groupLabel.key === 'current' || groupLabel.key === 'upcoming'"
                :href="instance.calendarLink"
                >Scheduled {{ instance.formattedDates }}</a
              >
            </span>
          </div>

          <!-- current instance actions -->
          <program-instance-actions
            v-if="groupLabel.key === 'current'"
            :id="instance.id"
            :entity-id="instance.planId"
            :enable-complete="instance.isLastDay"
            :enable-reschedule="planStatus === 'Live'"
            :reschedule-url="instance.rescheduleLink"
            :is-complete="!!instance.isComplete"
            :entity-category="entityCategory"
            :is-meal-plan="isMealPlan"
            @deleted="removeGrouping(instance.id, 'current')"
          />

          <!-- upcoming instance actions -->
          <program-instance-actions
            v-if="groupLabel.key === 'upcoming'"
            :id="instance.id"
            :entity-id="instance.planId"
            :enable-complete="false"
            :enable-reschedule="planStatus === 'Live'"
            :reschedule-url="instance.rescheduleLink"
            :is-complete="false"
            :entity-category="entityCategory"
            :is-meal-plan="isMealPlan"
            @deleted="removeGrouping(instance.id, 'upcoming')"
          />
        </div>
      </template>

      <!-- PAST INSTANCES -->
      <div v-if="resultsReady && groupings.pastCount > 0">
        <div
          v-for="instance in groupings.scheduledInstances.past.pastInstances"
          :key="`past_instance${instance.id}`"
          class="purchased-status-row top-border"
        >
          <div class="status-row-date">
            <span class="time">
              <i class="material-symbols-outlined" aria-hidden="true">schedule</i>
              Past
            </span>
            <span class="schedule">
              {{ `Scheduled ${instance.formattedDates}` }}
            </span>
          </div>

          <!-- past instance actions -->
          <program-instance-actions
            :id="instance.id"
            :entity-id="instance.planId"
            :enable-complete="true"
            :enable-reschedule="planStatus === 'Live' && instance.canReschedulePast"
            :reschedule-url="instance.rescheduleLink"
            :is-complete="!!instance.isComplete"
            :entity-category="entityCategory"
            :is-meal-plan="isMealPlan"
            @deleted="removeGrouping(instance.id, 'past')"
          />
        </div>
      </div>
      <div id="infinity-scroller" ref="infinityScroller" />
    </div>

    <!-- loading -->
    <div v-if="loading" class="purchased-status-row">
      <spinner-loader v-if="true" />
    </div>
  </section>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useCalendarStore } from '@/stores/calendar'
import { fbApi, objToSearchParams } from '@/utils/http-api'
import SpinnerLoader from '@/components/loaders/spinner-loader.vue'
import Callout from '@/components/notifications/callout.vue'
import ProgramInstanceActions from '@/components/buttons/program-instance-actions.vue'
import ProgramActions from '@/components/buttons/program-actions.vue'

/**
 * Fetches and lists scheduled instances for a specific plan (program, routine, challenge).
 */
export default defineComponent({
  name: 'PlansScheduledInstances',
  components: { SpinnerLoader, Callout, ProgramInstanceActions, ProgramActions },
  props: {
    planId: {
      type: Number,
      required: true,
    },
    planStatus: {
      type: String,
      required: true,
    },
    // this will be passed along as entityCategory (lowercased)
    brandName: {
      type: String,
      required: true,
    },
    isMealPlan: {
      type: Boolean,
      default: false,
    },
    planTitle: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      error: '',
      groupings: {},

      groupLabels: [
        {
          name: 'In Progress',
          key: 'current',
        },
        {
          name: 'Upcoming',
          key: 'upcoming',
        },
      ],

      // cursorHash contains the url hash to point to the next set of notifications to load
      cursorHash: null,
    }
  },

  computed: {
    ...mapState(useCalendarStore, ['refreshScheduledInstances']),

    resultsReady() {
      return !!(!this.error && this.groupings.scheduledInstances)
    },

    entityCategory() {
      return this.brandName.toLowerCase()
    },

    canLoadMore() {
      return (
        !this.loading &&
        this.groupings.scheduledInstances &&
        this.groupings.scheduledInstances.past &&
        this.groupings.scheduledInstances.past.hasMorePages &&
        this.groupings.scheduledInstances.past.pastInstances.length > 0
      )
    },
  },

  // watchers
  watch: {
    // when pinia store changes for refreshScheduledInstances
    // and it is true, re-fetch the schedule and flip the flag back to false
    refreshScheduledInstances(newVal, oldVal) {
      if (newVal !== oldVal && newVal === true) {
        this.cursorHash = null
        this.fetchScheduled()
        this.setRefreshScheduledInstances(false)
      }
    },
  },

  // lifecycle
  created() {
    this.fetchScheduled()
  },

  mounted() {
    // add event listener to show / hide based on scroll position
    document.addEventListener('scroll', this.infinityScroll, {
      passive: true,
    })
  },

  beforeUnmount() {
    // clean up
    document.removeEventListener('scroll', this.infinityScroll)
  },

  // component methods
  methods: {
    ...mapActions(useCalendarStore, ['setRefreshScheduledInstances']),

    /**
     * Get user scheduled groupings for a specifc plan type
     * @returns {Promise<boolean>}
     */
    async fetchScheduled() {
      // do not process delete twice
      if (this.loading) {
        return
      }

      // reset state (even data)
      this.loading = true
      // this.groupings = {}
      this.error = ''

      let params = {}

      if (this.cursorHash) {
        // nextPageUrlHash is manipulated to retrieve the next cursor hash for url
        // see ApiOutputTrait::uiCursorPagination
        params['cursor'] = this.cursorHash
      }

      try {
        const res = await fbApi.get(`/plans/${this.planId}/scheduled`, {
          searchParams: objToSearchParams(params),
        })

        if (!res || !res.body || !res.body.data) {
          throw Error('Error getting scheduled instances')
        }

        const scheduledInstancesPast = res.body.data.scheduledInstances.past

        // on initial load, first page, just set results
        if (!this.cursorHash) {
          this.groupings = res.body.data
        } else {
          // if loading more scheduled instances, concat new scheduled instances to end of current set
          const pastInstances = this.groupings.scheduledInstances.past.pastInstances.concat(
            scheduledInstancesPast.pastInstances,
          )

          // update groupings with ONLY the newly concat instances and updated hasMorePages for pagination,
          // as the API endpoint will return counts of 0 as we're doing some efficient logic to
          // minimize queries to the database
          this.groupings.scheduledInstances.past = {
            ...this.groupings.scheduledInstances.past,
            hasMorePages: scheduledInstancesPast.hasMorePages,
            pastInstances,
          }
        }

        // update cursorHash
        this.cursorHash = scheduledInstancesPast.nextPageUrlHash

        // this.groupings = res.body.data
      } catch (e) {
        this.error = 'Error getting scheduled instances'
      }

      // cancel loading
      this.loading = false
    },

    loadMore() {
      this.fetchScheduled()
    },

    removeGrouping(groupingId, groupingType) {
      // remove from state
      if (groupingType === 'past') {
        // past
        this.groupings.scheduledInstances.past.pastInstances =
          this.groupings.scheduledInstances.past.pastInstances.filter(
            (planGrouping) => planGrouping.id !== groupingId,
          )

        // update counts, calculated slightly differently due to pagination count
        const newPastCount = this.groupings.pastCount - 1
        this.groupings.pastCount = newPastCount > 0 ? newPastCount : 0
      } else {
        // current and upcoming
        this.groupings.scheduledInstances[groupingType] = this.groupings.scheduledInstances[
          groupingType
        ].filter((planGrouping) => planGrouping.id !== groupingId)

        // update inProgress boolean
        this.groupings.inProgress = this.groupings.scheduledInstances.current.length > 0

        // update counts
        const newUpcomingCount = this.groupings.scheduledInstances.upcoming.length
        this.groupings.upcomingCount = newUpcomingCount > 0 ? newUpcomingCount : 0
      }
    },

    infinityScroll() {
      // short circuit if already loading
      if (!this.canLoadMore) {
        return
      }

      // look for empty div above load more
      const target = this.$refs.infinityScroller

      // in case of no results, ref will not exist
      if (!target) {
        return
      }

      const targetLocation = target.getBoundingClientRect()
      let inView = false

      // checking both window and documentElement height/width
      // Modern browsers tend to use window.innerHeight, however some older
      // browser versions utilize document.documentElement and vary in calculation methods
      // between the two. Checking both here to cover most bases
      inView =
        targetLocation.top >= 0 &&
        targetLocation.left >= 0 &&
        targetLocation.bottom <=
          (window.innerHeight ||
            (document.documentElement && document.documentElement.clientHeight)) &&
        targetLocation.right <=
          (window.innerWidth || (document.documentElement && document.documentElement.clientWidth))

      if (inView && this.groupings.scheduledInstances.past.hasMorePages) {
        this.loadMore()
      }
    },
  },
})
</script>
